import React, { useState } from 'react';
import { supabase } from '../lib/supabase';

interface EmailVerificationPendingProps {
  email: string;
  onBackToLogin: () => void;
}

const EmailVerificationPending: React.FC<EmailVerificationPendingProps> = ({ email, onBackToLogin }) => {
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  const handleResendEmail = async () => {
    setIsResending(true);
    setResendMessage('');

    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
      });

      if (error) {
        setResendMessage(`Error: ${error.message}`);
      } else {
        setResendMessage('Verification email sent! Please check your inbox.');
      }
    } catch (error) {
      setResendMessage('Failed to resend email. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-300 via-yellow-400 to-yellow-500 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
        <div className="text-center">
          <div className="w-20 h-20 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 19v-8.93a2 2 0 01.89-1.664l7-4.666a2 2 0 012.22 0l7 4.666A2 2 0 0121 10.07V19M3 19a2 2 0 002 2h14a2 2 0 002-2M3 19l6.75-4.5M21 19l-6.75-4.5M3 10l6.75 4.5M21 10l-6.75 4.5m0 0l-1.14.76a2 2 0 01-2.22 0l-1.14-.76" />
            </svg>
          </div>
          
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Check Your Email</h2>
          <p className="text-gray-600 mb-6">
            We've sent a verification email to:
            <br />
            <span className="font-medium text-gray-800">{email}</span>
          </p>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-700">
              Please click the link in the email to verify your account. 
              Once verified, you can sign in to TraqPack.
            </p>
          </div>

          <div className="space-y-3">
            <button
              onClick={handleResendEmail}
              disabled={isResending}
              className="w-full py-3 px-4 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 text-gray-800 font-medium rounded-lg transition-colors duration-200 disabled:cursor-not-allowed"
            >
              {isResending ? 'Sending...' : 'Resend Verification Email'}
            </button>

            <button
              onClick={onBackToLogin}
              className="w-full py-3 px-4 bg-yellow-400 hover:bg-yellow-500 text-black font-medium rounded-lg transition-colors duration-200"
            >
              Back to Login
            </button>
          </div>

          {resendMessage && (
            <div className={`mt-4 p-3 rounded-lg text-sm ${
              resendMessage.includes('Error') 
                ? 'bg-red-50 text-red-700 border border-red-200' 
                : 'bg-green-50 text-green-700 border border-green-200'
            }`}>
              {resendMessage}
            </div>
          )}

          <div className="mt-6 text-xs text-gray-500">
            <p>Didn't receive the email?</p>
            <ul className="mt-2 text-left space-y-1">
              <li>• Check your spam folder</li>
              <li>• Make sure {email} is correct</li>
              <li>• Wait a few minutes and try resending</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationPending;